import { useState } from "react";
import { useWallet } from "@/context/WalletContext";
import { createLock, fmtNum, fmtUsd, shortAddr } from "@/lib/api";
import { PageHeader } from "@/dashboard/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { RobinhoodMark } from "@/components/RobinhoodMark";
import { toast } from "sonner";
import { Loader2, Check, ArrowRight, Lock as LockIcon, ShieldCheck } from "lucide-react";

const PRESET_TOKENS = ["WLK", "RBH", "USDC", "ETH", "GLD", "LPX"];

export default function LockerView({ product, meta }) {
  const { address } = useWallet();
  const isLp = product.startsWith("lp");
  const [form, setForm] = useState({
    token_symbol: "WLK",
    token_address: "",
    amount: "",
    recipient: address || "",
    unlock_date: "",
    schedule: product.includes("vesting") ? "linear" : "cliff",
  });
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | pending | success
  const [result, setResult] = useState(null);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.token_address.match(/^0x[a-fA-F0-9]{40}$/)) e.token_address = "Enter a valid contract address";
    if (!form.amount || Number(form.amount) <= 0) e.amount = "Amount must be greater than zero";
    if (!form.recipient.match(/^0x[a-fA-F0-9]{40}$/)) e.recipient = "Enter a valid recipient address";
    if (!form.unlock_date) e.unlock_date = "Select an unlock date";
    else if (new Date(form.unlock_date) <= new Date()) e.unlock_date = "Unlock date must be in the future";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openConfirm = () => {
    if (validate()) setConfirmOpen(true);
  };

  const submit = async () => {
    setState("pending");
    try {
      const payload = {
        wallet: address,
        product,
        token_symbol: form.token_symbol,
        token_address: form.token_address,
        amount: Number(form.amount),
        recipient: form.recipient,
        unlock_date: new Date(form.unlock_date).toISOString(),
        schedule: form.schedule,
      };
      const res = await createLock(payload);
      setResult(res);
      setState("success");
      toast.success("Lock created", { description: `${fmtNum(res.amount)} ${res.token_symbol} secured` });
    } catch (err) {
      setState("idle");
      toast.error("Failed to create lock");
    }
  };

  const reset = () => {
    setConfirmOpen(false);
    setState("idle");
    setResult(null);
    setForm({ token_symbol: "WLK", token_address: "", amount: "", recipient: address || "", unlock_date: "", schedule: form.schedule });
  };

  const inputCls = "border-welock-border bg-welock-bg font-mono text-sm text-welock-textPrimary focus-visible:ring-welock-accent";

  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="create lock" title={meta.title} desc={meta.desc} />

      <div className="border border-welock-border bg-welock-surface p-6 md:p-8">
        <div className="space-y-6">
          {/* Token */}
          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">
              {isLp ? "LP token symbol" : "Token"}
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PRESET_TOKENS.map((t) => (
                <button
                  key={t}
                  data-testid={`token-${t}`}
                  onClick={() => set("token_symbol", t)}
                  className={`border px-3 py-1.5 font-mono text-xs transition-colors ${
                    form.token_symbol === t
                      ? "border-welock-accent bg-welock-accent/10 text-welock-accent"
                      : "border-welock-border text-welock-textSecondary hover:border-welock-hairline"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="token_address" className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">
                {isLp ? "Pool contract" : "Token contract"}
              </Label>
              <Input
                id="token_address"
                data-testid="input-token-address"
                placeholder="0x..."
                value={form.token_address}
                onChange={(e) => set("token_address", e.target.value)}
                className={`mt-2 ${inputCls}`}
              />
              {errors.token_address && <p className="mt-1.5 font-mono text-[11px] text-welock-danger">{errors.token_address}</p>}
            </div>
            <div>
              <Label htmlFor="amount" className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">
                {isLp ? "LP amount" : "Amount"}
              </Label>
              <Input
                id="amount"
                data-testid="input-amount"
                type="number"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className={`mt-2 ${inputCls}`}
              />
              {errors.amount && <p className="mt-1.5 font-mono text-[11px] text-welock-danger">{errors.amount}</p>}
            </div>
          </div>

          <div>
            <Label htmlFor="recipient" className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">
              Recipient / beneficiary
            </Label>
            <Input
              id="recipient"
              data-testid="input-recipient"
              placeholder="0x..."
              value={form.recipient}
              onChange={(e) => set("recipient", e.target.value)}
              className={`mt-2 ${inputCls}`}
            />
            {errors.recipient && <p className="mt-1.5 font-mono text-[11px] text-welock-danger">{errors.recipient}</p>}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="unlock_date" className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">
                Unlock date
              </Label>
              <Input
                id="unlock_date"
                data-testid="input-unlock-date"
                type="datetime-local"
                value={form.unlock_date}
                onChange={(e) => set("unlock_date", e.target.value)}
                className={`mt-2 ${inputCls} [color-scheme:dark]`}
              />
              {errors.unlock_date && <p className="mt-1.5 font-mono text-[11px] text-welock-danger">{errors.unlock_date}</p>}
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">Release schedule</Label>
              <div className="mt-2 flex gap-2">
                {["cliff", "linear"].map((s) => (
                  <button
                    key={s}
                    data-testid={`schedule-${s}`}
                    onClick={() => set("schedule", s)}
                    className={`flex-1 border px-3 py-2 font-mono text-xs capitalize transition-colors ${
                      form.schedule === s
                        ? "border-welock-accent bg-welock-accent/10 text-welock-accent"
                        : "border-welock-border text-welock-textSecondary hover:border-welock-hairline"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-welock-border pt-6">
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-welock-textMuted">
              <ShieldCheck size={13} /> Non custodial. On Robinhood Chain.
            </span>
            <button
              data-testid="create-lock-submit"
              onClick={openConfirm}
              className="group flex items-center gap-2 bg-welock-accent px-6 py-3 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover"
            >
              Review lock
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Confirm / success modal */}
      <Dialog open={confirmOpen} onOpenChange={(o) => (!o && state !== "pending" ? reset() : null)}>
        <DialogContent data-testid="confirm-modal" className="max-w-md border-welock-border bg-welock-surface p-0 text-welock-textPrimary [&>button]:text-welock-textMuted">
          {state !== "success" ? (
            <>
              <div className="border-b border-welock-border p-6">
                <div className="flex items-center gap-2.5">
                  <LockIcon size={18} className="text-welock-accent" />
                  <span className="font-heading text-base font-semibold">Confirm {meta.title}</span>
                </div>
                <p className="mt-2 font-mono text-xs text-welock-textMuted">Review before signing. This cannot be reversed before the unlock date.</p>
              </div>
              <div className="space-y-3 p-6 font-mono text-sm">
                <Row k="Asset" v={form.token_symbol} />
                <Row k="Amount" v={fmtNum(Number(form.amount))} />
                <Row k="Recipient" v={shortAddr(form.recipient)} />
                <Row k="Unlock" v={new Date(form.unlock_date).toLocaleString()} />
                <Row k="Schedule" v={form.schedule} cap />
                <Row k="Network" v={<RobinhoodMark size={14} withLabel />} />
              </div>
              <div className="flex gap-3 border-t border-welock-border p-6">
                <button
                  data-testid="confirm-cancel"
                  disabled={state === "pending"}
                  onClick={() => setConfirmOpen(false)}
                  className="flex-1 border border-welock-border py-3 font-mono text-sm text-welock-textSecondary transition-colors hover:text-welock-textPrimary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-lock"
                  disabled={state === "pending"}
                  onClick={submit}
                  className="flex flex-1 items-center justify-center gap-2 bg-welock-accent py-3 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-70"
                >
                  {state === "pending" ? (<><Loader2 size={15} className="animate-spin" /> Signing</>) : "Confirm lock"}
                </button>
              </div>
            </>
          ) : (
            <div className="p-8 text-center" data-testid="lock-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center border border-welock-accent/40 bg-welock-accent/10">
                <Check size={26} className="text-welock-accent" />
              </div>
              <h3 className="mt-6 font-heading text-lg text-welock-textPrimary">Lock created</h3>
              <p className="mt-2 font-mono text-xs text-welock-textMuted">
                {fmtNum(result.amount)} {result.token_symbol} locked. Value {fmtUsd(result.value_usd)}.
              </p>
              <div className="mt-6 space-y-2 border border-welock-border bg-welock-bg p-4 text-left font-mono text-xs">
                <Row k="Lock ID" v={result.id.slice(0, 12)} />
                <Row k="Status" v="Active" />
                <Row k="Unlock" v={new Date(result.unlock_date).toLocaleDateString()} />
              </div>
              <button
                data-testid="success-done"
                onClick={reset}
                className="mt-6 w-full bg-welock-accent py-3 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover"
              >
                Create another
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v, cap }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-welock-textMuted">{k}</span>
      <span className={`text-welock-textPrimary ${cap ? "capitalize" : ""}`}>{v}</span>
    </div>
  );
}
